import { useEffect } from "react";
import { useLocation } from "react-router";
import { matchRoutes } from "react-router-config";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { updateModuleName, setMetaIndex } from "@App/appReducer";
import {
  persistEngageAttributionFromUrl,
  registerPostHogSuperProperties,
  setLastSearchedRegionForPostHog,
  setLastVisitedPropertyForPostHog,
  trackRoutePageView,
} from "@Utils/posthog";
import { APP_PAGE } from "@Utils/webApputils/analyticEvents";

// Pages that handle their own page_viewed event (e.g. AB test variants with different screen names)
const SELF_TRACKED_PAGES = [APP_PAGE.SCHOLARSHIP];

const DataLayout = (props: any) => {
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const { routes } = props;
    const routeData = matchRoutes(routes, location.pathname).reduce(
      (res: any, { route, match }: any) => {
        if (match.isExact) {
          return { route, match };
        }
        return res;
      },
      null,
    );

    persistEngageAttributionFromUrl();
    registerPostHogSuperProperties().catch(() => undefined);

    // Seed meta.index from the route's `indexable` flag on CSR navigations. Use setMetaIndex
    // (merge) — never getMetaSuccess({ index }) which would wipe title/description/canonical.
    // Page loadData/fetch that calls getMetaSuccess afterwards still wins with a full replace.
    if (typeof routeData?.route?.indexable === "boolean") {
      dispatch(setMetaIndex(routeData.route.indexable));
    }

    const moduleName = routeData?.route?.moduleName;
    if (moduleName) {
      // @ts-ignore
      window.moduleName = moduleName;
      dispatch(updateModuleName(moduleName));
      if (!SELF_TRACKED_PAGES.includes(moduleName)) {
        trackRoutePageView(moduleName);
      }
    }

    if (routeData?.route?.prefetch) {
      setTimeout(() => {
        routeData?.route?.prefetch?.forEach((it: any) => {
          it.preload();
        });
      }, 1000);
    }
  }, [location.pathname]);

  // Both halves come off one store object, so they always describe the same place.
  const { propertyName, propertySlug, regionName, regionSlug } = useSelector(
    ({ propertyPageData, searchPageData }: any) => ({
      propertyName: propertyPageData?.data?.name,
      propertySlug: propertyPageData?.data?.canonical_name,
      regionName: searchPageData?.region?.name,
      regionSlug: searchPageData?.region?.canonical_name,
    }),
    shallowEqual,
  );

  // One effect per pair: the store is not cleared on navigation, so a shared one would drag the other along.
  useEffect(() => {
    setLastVisitedPropertyForPostHog(propertySlug, propertyName);
  }, [propertyName, propertySlug]);

  useEffect(() => {
    setLastSearchedRegionForPostHog(regionSlug, regionName);
  }, [regionName, regionSlug]);

  // Flushed once, after both writes.
  useEffect(() => {
    if (!propertyName && !regionName) return;
    registerPostHogSuperProperties().catch(() => undefined);
  }, [propertyName, propertySlug, regionName, regionSlug]);

  return <></>;
};

export default DataLayout;
